/**
 * main.tsx -- Dashboard application entry point (T368 / INT-405).
 *
 * Mounts AppShell inside ConvexAuthProvider on the #root DOM element.
 * ConvexAuthProvider sits OUTSIDE AppShell so that auth state is available
 * to AuthGate (the first meaningful provider inside the shell).
 *
 * Global stylesheet import order is load-bearing:
 *   1. tokens.css      -- CSS custom-property declarations (Cohere 2026 token vocabulary)
 *   2. animations.css  -- Legacy keyframe stub (retained for import compatibility)
 *   3. motion.css      -- Central motion stylesheet (20 keyframes + utility classes)
 *   4. responsive.css  -- Six-band responsive layer + display-switching classes (FR-004/FR-005)
 *   5. components.css  -- Component-scoped rules (skip-link, TopNav active underline, etc.)
 *
 * Sentry init is a side-effect import and must run before any React rendering
 * so the error boundary can capture early lifecycle errors. The init module
 * was relocated from the legacy repo-root `src/sentry.ts` to
 * `apps/dashboard/src/sentry.ts` when the legacy `src/` tree was decommissioned.
 */

// -- Side-effect: Sentry init (must precede React rendering) --
import "./sentry";

// -- Global stylesheets (order matters) --
import "./styles/tokens.css";
import "./styles/animations.css";
import "./styles/motion.css";
import "./styles/responsive.css";
import "./styles/components.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { ConvexReactClient } from "convex/react";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { AppShell } from "./AppShell";

// -- Convex client --
const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL as string,
);

// -- Mount --
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error(
    "Root element #root not found. Ensure index.html contains <div id=\"root\"></div>.",
  );
}

createRoot(rootElement).render(
  <React.StrictMode>
    <ConvexAuthProvider client={convex}>
      <AppShell />
    </ConvexAuthProvider>
  </React.StrictMode>,
);

// ---------------------------------------------------------------------------
// Service worker — conditional registration behind ff:service-worker-enabled
// ---------------------------------------------------------------------------

if (
  "serviceWorker" in navigator &&
  (() => {
    try {
      return localStorage.getItem("ff:service-worker-enabled") === "true";
    } catch {
      return false;
    }
  })()
) {
  navigator.serviceWorker.register("/sw.js").catch((err) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[sw] Service worker registration failed:", err);
    }
  });
}
